import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
    SovosBox,
    SovosButton,
    SovosTypography,
    SovosDivider,
    SovosIconButton,
    SovosDialog,
    SovosDialogTitle,
    SovosDialogContent,
    SovosDialogActions,
    SovosSnackbar,
    SovosTextField,
    SovosSelect,
    SovosMenuItem,
    SovosPopover,
    SovosStepper,
    SovosStep,
    SovosStepLabel,
    SovosStepButton,
    SovosToolbarGroup,
    SovosToolbarActions,
    SovosToolbarColumn,
    SovosToolbarFilter,
    SovosCountryFlag,
} from "mosaic-react";
import {
    CheckCircle,
    KeyboardArrowDown,
    Search,
    Percent,
    TextFields,
    CalendarToday,
    Tag,
    VisibilityOff,
    Visibility,
    Close,
    Undo,
    Warning,
} from "mosaic-react-icons";
import { useGridApiContext, GridPreferencePanelsValue } from "@mui/x-data-grid";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

const DO_NOT_IMPORT = "__DO_NOT_IMPORT__";

const STEPS = ["Map entity", "Map columns", "Data validation"];

const FieldTypeIcon = ({ type, size = 14 }) => {
    const sx = { fontSize: size, color: "text.secondary" };
    if (type === "number") return <Percent sx={sx} />;
    if (type === "text") return <TextFields sx={sx} />;
    if (type === "date") return <CalendarToday sx={sx} />;
    return <Tag sx={sx} />;
};


// ─── KPI Stats Bar ───────────────────────────────────────────────────────────

const KPI_BORDER_COLORS = ["warning.main", "success.main", "text.disabled", "primary.main"];

const StatTile = ({ label, value, borderColor }) => (
    <SovosBox
        sx={{
            flex: 1,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            borderBottom: "3px solid",
            borderBottomColor: borderColor,
            bgcolor: "background.paper",
        }}
    >
        <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {label}
        </SovosTypography>
        <SovosTypography variant="h5" fontWeight={700}>
            {value}
        </SovosTypography>
    </SovosBox>
);

// ─── Mapping dropdown ─────────────────────────────────────────────────────────

const MappingDropdown = ({ csvColumn, displayName, value, originalValue, mschFields, usedFields, onChange, onRevert, i18n }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState("");

    const open = Boolean(anchorEl);

    const selectedField = value === DO_NOT_IMPORT
        ? { name: "Do not import", type: null }
        : mschFields.find((f) => f.id === value);

    const matchName = displayName || csvColumn;
    const recommendedFields = mschFields.filter((f) => f.recommended?.includes(matchName));
    const filteredRecommended = recommendedFields.filter(
        (f) => search === "" || f.name.toLowerCase().includes(search.toLowerCase())
    );
    const otherFields = mschFields.filter(
        (f) => !f.recommended?.includes(csvColumn) &&
            (search === "" || f.name.toLowerCase().includes(search.toLowerCase()))
    );

    const handleOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const handleClose = () => { setAnchorEl(null); setSearch(""); };

    const handleSelect = (fieldId) => {
        onChange(csvColumn, fieldId);
        handleClose();
    };

    const isUsed = (fieldId) => usedFields.includes(fieldId) && value !== fieldId;

    const isMapped = !!value;
    const isDoNotImport = value === DO_NOT_IMPORT;
    const buttonLabel = selectedField ? selectedField.name : "Map to";
    const isDirty = value !== originalValue;

    return (
        <>
            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                <SovosBox
                    component="button"
                    onClick={handleOpen}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: isMapped && !isDoNotImport ? "primary.main" : "divider",
                        bgcolor: isMapped && !isDoNotImport ? "primary.50" : "background.paper",
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 0,
                        fontFamily: "inherit",
                        "&:hover": { borderColor: "primary.main" },
                    }}
                >
                    {isMapped && !isDoNotImport && selectedField && (
                        <FieldTypeIcon type={selectedField.type} size={13} />
                    )}
                    <SovosTypography
                        variant="caption"
                        color={isMapped && !isDoNotImport ? "primary.main" : "text.secondary"}
                        sx={{ flex: 1, textAlign: "left", fontWeight: isMapped ? 600 : 400 }}
                        noWrap
                        title={buttonLabel}
                    >
                        {buttonLabel}
                    </SovosTypography>
                    <KeyboardArrowDown sx={{ fontSize: 16, color: "text.secondary" }} />
                </SovosBox>
                {isDirty && (
                    <SovosIconButton
                        size="small"
                        tooltipText="Revert to original"
                        onClick={(e) => { e.stopPropagation(); onRevert(csvColumn); }}
                        sx={{ flexShrink: 0 }}
                    >
                        <Undo sx={{ fontSize: 14 }} />
                    </SovosIconButton>
                )}
            </SovosBox>

            <SovosPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { width: 260, overflow: "hidden" } }}
            >
                {/* Search */}
                <SovosBox sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <SovosTextField
                        size="small"
                        fullWidth
                        placeholder="Search column name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ fontSize: 16, mr: 0.5, color: "text.disabled" }} />,
                        }}
                        autoFocus
                    />
                </SovosBox>

                <SovosBox sx={{ maxHeight: 260, overflowY: "auto" }}>
                    {/* Do not import */}
                    <DropdownItem
                        label="Do not import"
                        type={null}
                        selected={value === DO_NOT_IMPORT}
                        onClick={() => handleSelect(DO_NOT_IMPORT)}
                    />
                    <SovosDivider />

                    {/* Recommended */}
                    {filteredRecommended.length > 0 && (
                        <>
                            {filteredRecommended.map((f) => (
                                <DropdownItem
                                    key={f.id}
                                    label={f.name}
                                    type={f.type}
                                    recommended
                                    disabled={isUsed(f.id)}
                                    selected={value === f.id}
                                    onClick={() => !isUsed(f.id) && handleSelect(f.id)}
                                />
                            ))}
                            {otherFields.length > 0 && <SovosDivider />}
                        </>
                    )}

                    {/* Other fields */}
                    {otherFields.map((f) => (
                        <DropdownItem
                            key={f.id}
                            label={f.name}
                            type={f.type}
                            disabled={isUsed(f.id)}
                            selected={value === f.id}
                            onClick={() => !isUsed(f.id) && handleSelect(f.id)}
                        />
                    ))}

                    {filteredRecommended.length === 0 && otherFields.length === 0 && (
                        <SovosBox sx={{ px: 2, py: 1.5 }}>
                            <SovosTypography variant="caption" color="text.disabled">
                                {i18n`No fields match`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>
            </SovosPopover>
        </>
    );
};

const DropdownItem = ({ label, type, recommended, disabled, selected, onClick }) => (
    <SovosBox
        component="button"
        onClick={onClick}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            px: 1.5,
            py: 0.875,
            border: "none",
            bgcolor: selected ? "primary.50" : "transparent",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.4 : 1,
            fontFamily: "inherit",
            "&:hover": !disabled ? { bgcolor: selected ? "primary.50" : "action.hover" } : {},
        }}
    >
        {type ? (
            <FieldTypeIcon type={type} size={14} />
        ) : (
            <SovosBox sx={{ width: 14 }} />
        )}
        <SovosTypography
            variant="caption"
            color={selected ? "primary.main" : "text.primary"}
            fontWeight={selected ? 600 : 400}
            sx={{ flex: 1, textAlign: "left" }}
            noWrap
        >
            {label}
        </SovosTypography>
        {recommended && (
            <SovosBox
                sx={{
                    fontSize: 10,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: "success.50",
                    color: "success.dark",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                }}
            >
                Recommended
            </SovosBox>
        )}
    </SovosBox>
);

// ─── DataGrid header/items builders ──────────────────────────────────────────

const buildDgItems = (csvRows, columns) =>
    csvRows.map((row, index) => ({
        id: row[columns[0]] || `${index}`,
        uniqueId: `${index}`,
        card: columns.map((col) => [{ value: row[col] ?? "" }]),
    }));

// ─── Confirm dialog ───────────────────────────────────────────────────────────

const ConfirmDialog = ({ open, onCancel, onConfirm, mappings, mschFields, i18n }) => {
    const mappedEntries = Object.entries(mappings).filter(([, v]) => v && v !== DO_NOT_IMPORT);
    return (
        <SovosDialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                <SovosIconButton size="small" tooltipText="Close" onClick={onCancel}>
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>
            <SovosDialogTitle>{i18n`Confirm your column mapping?`}</SovosDialogTitle>
            <SovosDialogContent>
                <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {i18n`Please review the column mapping below before proceeding to data validation.`}
                </SovosTypography>
                <SovosBox
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                        mb: 1,
                    }}
                >
                    {mappedEntries.map(([col, fieldId], idx) => {
                        const field = mschFields.find((f) => f.id === fieldId);
                        const sourceLabel = stripCdmPrefix(col);
                        const targetLabel = field?.name || fieldId;
                        return (
                            <SovosBox
                                key={col}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                }}
                            >
                                <SovosTypography variant="caption" fontWeight={600} sx={{ flex: 1 }} noWrap>
                                    {sourceLabel}
                                </SovosTypography>
                                <SovosTypography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>→</SovosTypography>
                                <SovosTypography variant="caption" sx={{ flex: 1 }} noWrap>
                                    {targetLabel}
                                </SovosTypography>
                            </SovosBox>
                        );
                    })}
                </SovosBox>
            </SovosDialogContent>
            <SovosDialogActions>
                <SovosButton variant="text" onClick={onCancel}>
                    {i18n`Cancel`}
                </SovosButton>
                <SovosButton variant="contained" onClick={onConfirm}>
                    {i18n`Confirm column mapping`}
                </SovosButton>
            </SovosDialogActions>
        </SovosDialog>
    );
};

const stripCdmPrefix = (col) => col.replace(/^CDM_\w+\./, "");

const DATATYPE_TO_FIELD_TYPE = {
    DECIMAL: "number", NUMERIC: "number", INTEGER: "number", INT: "number",
    BIGINT: "number", FLOAT: "number", DOUBLE: "number", REAL: "number",
    VARCHAR: "text", TEXT: "text", CHAR: "text", STRING: "text",
    DATE: "date", DATETIME: "date", TIMESTAMP: "date",
};

const looksLikeNumber = (value) => value !== "" && !isNaN(Number(value));
const looksLikeDate = (value) => !isNaN(Date.parse(value));

const isCellMismatchResolved = (value, targetType, sourceType) => {
    if (value === "" || value == null) return true;

    if (targetType === "number") return looksLikeNumber(value);
    if (targetType === "date") return looksLikeDate(value);

    if (targetType === "text") {
        if (sourceType === "number") return !looksLikeNumber(value);
        if (sourceType === "date") return !looksLikeDate(value);
    }
    return true;
};

const WarningSkipDialog = ({ open, onCancel, onConfirm, skippedColumns, warningRows, tableColumns, i18n }) => {
    const hasSkipped = skippedColumns.length > 0;
    const hasWarnings = warningRows.length > 0;

    return (
        <SovosDialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                <SovosIconButton size="small" tooltipText="Close" onClick={onCancel}>
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>
            <SovosDialogTitle>Some data will be skipped</SovosDialogTitle>
            <SovosDialogContent>
                <SovosBox
                    sx={{
                        border: "1px dashed",
                        borderColor: "primary.light",
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                    }}
                >
                    {hasSkipped && (
                        <SovosBox sx={{ mb: hasWarnings ? 2 : 0 }}>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Warning sx={{ color: "warning.main", fontSize: 18 }} />
                                <SovosTypography variant="body2" color="text.secondary">
                                    {i18n`The following columns will not be imported`}
                                </SovosTypography>
                            </SovosBox>
                            {skippedColumns.map((col) => (
                                <SovosTypography key={col} variant="h6" fontWeight={600} sx={{ ml: 3.5 }}>
                                    {stripCdmPrefix(col)}
                                </SovosTypography>
                            ))}
                        </SovosBox>
                    )}

                    {hasWarnings && (
                        <SovosBox>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <Warning sx={{ color: "warning.main", fontSize: 18 }} />
                                <SovosTypography variant="body2" color="text.secondary">
                                    The following rows still have warnings and will be skipped
                                </SovosTypography>
                            </SovosBox>
                            <SovosBox sx={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "inherit" }}>
                                    <thead>
                                        <tr>
                                            {tableColumns.map((col) => (
                                                <th
                                                    key={col}
                                                    style={{
                                                        textAlign: "left",
                                                        padding: "4px 12px",
                                                        color: "rgba(0,0,0,0.6)",
                                                        fontWeight: 400,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {stripCdmPrefix(col)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {warningRows.map(({ rowId, data, warnings }) => (
                                            <tr key={rowId}>
                                                {tableColumns.map((col) => (
                                                    <td
                                                        key={col}
                                                        style={{
                                                            padding: "4px 12px",
                                                            backgroundColor: warnings[col]
                                                                ? "rgba(211, 47, 47, 0.08)"
                                                                : "transparent",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {data?.[col] ?? ""}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </SovosBox>
                        </SovosBox>
                    )}
                </SovosBox>

                <SovosBox
                    sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                    }}
                >
                    <SovosTypography variant="body2" color="text.secondary">
                        {i18n`The skipped data will not be imported into the framework. Are you sure you want to continue?`}
                    </SovosTypography>
                </SovosBox>
            </SovosDialogContent>
            <SovosDialogActions>
                <SovosButton variant="text" onClick={onCancel}>
                    {i18n`Cancel`}
                </SovosButton>
                <SovosButton variant="contained" onClick={onConfirm}>
                    {i18n`Skip and proceed`}
                </SovosButton>
            </SovosDialogActions>
        </SovosDialog>
    );
};

const ChooseEntityCard = ({ onNext, onCancel, i18n, projectModules, availableConfigSets: externalConfigSets, onFetchConfigSets, onFetchEntities, initialConfigSet, initialEntityName }) => {
    const [configSet, setConfigSet] = useState(initialConfigSet || "");
    const [entityName, setEntityName] = useState(initialEntityName || "");
    const [fileType, setFileType] = useState("");
    const [entities, setEntities] = useState([]);
    const [loadingEntities, setLoadingEntities] = useState(false);
    const [country, setCountry] = useState(initialConfigSet || "");

    const canProceed = country && entityName;


    useEffect(() => {
        if (!initialConfigSet) return;
        onFetchEntities(initialConfigSet).then((result) => {
            const fetched = result || [];
            setEntities(fetched);
            if (initialEntityName) {
                const match = fetched.find((e) => e.entity_name === initialEntityName);
                setFileType(match?.file_type || "");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialConfigSet]);

    const handleCountryChange = async (value) => {
        setCountry(value);
        setConfigSet(value);
        setEntityName("");
        setFileType("");
        setEntities([]);
        if (!value) return;
        setLoadingEntities(true);
        const result = await onFetchEntities(value);
        setEntities(result || []);
        setLoadingEntities(false);
    };

    const handleEntityChange = (val) => {
        setEntityName(val);
        const match = entities.find((e) => e.entity_name === val);
        setFileType(match?.file_type || "");
    };

    const handleNext = () => {
        onNext({ configSet, entityName, fileType, entityLabel: entityName });
    };

    const moduleOptions = externalConfigSets !== null
        ? externalConfigSets
        : projectModules
            .filter((m) => m.name && !["repository", "overview_dashboard", "merge_collisions", "merge_history", "aggregate_org_data", "generic_generator", "custom_templates", "new_navigation", "column_mapping"].includes(m.name))
            .map((m) => ({ module: m.name, config_set: m.name, msch_prefix: "", viewMode: m.viewMode }));
    const sourceModule = moduleOptions.filter((module) => module.viewMode === "source");

    return (
        <SovosBox
            sx={{
                position: "relative",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                width: 480,
                maxWidth: "90vw",
            }}
        >
            <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                <SovosIconButton size="small" tooltipText="Close" onClick={onCancel}>
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>

            <SovosTypography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                {i18n`Start column mapping by choosing entity`}
            </SovosTypography>
            <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {i18n`Choose what to map data against (the government schema). We recommend selecting entities based on your column mapping results.`}
            </SovosTypography>

            <SovosSelect
                label="Country"
                value={country || ""}
                onChange={(e) => handleCountryChange(e.target.value)}
                fullWidth
                sx={{ mb: 2.5 }}
                renderValue={(selected) => {
                    const code = selected.replace("CSV_", "");
                    return (
                        <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SovosCountryFlag flagHeight={"20px"} countryCode={code} />
                            <SovosTypography variant="body2">{code}</SovosTypography>
                        </SovosBox>
                    );
                }}
            >
                {(sourceModule || []).map((sourceMod) => {
                    const countryCode = sourceMod?.config_set.replace("CSV_", "");
                    return (
                        <SovosMenuItem key={sourceMod?.config_set} value={sourceMod?.config_set}>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <SovosCountryFlag
                                    data-testid="country-flag"
                                    flagHeight={"20px"}
                                    countryCode={countryCode}
                                />
                                <SovosTypography variant="body2">{countryCode}</SovosTypography>
                            </SovosBox>
                        </SovosMenuItem>
                    )
                })}
            </SovosSelect>

            <SovosSelect
                label="Entity"
                value={entityName}
                onChange={(e) => handleEntityChange(e.target.value)}
                fullWidth
                disabled={!configSet || loadingEntities}
                sx={{ mb: 3 }}
            >
                {loadingEntities
                    ? <SovosMenuItem value="" disabled>Loading...</SovosMenuItem>
                    : entities.map((e) => (
                        <SovosMenuItem key={e.entity_name} value={e.entity_name}>{e.entity_name}</SovosMenuItem>
                    ))
                }
            </SovosSelect>

            <SovosBox sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <SovosButton variant="text" onClick={onCancel}>Cancel</SovosButton>
                <SovosButton variant="contained" disabled={!canProceed} onClick={handleNext}>
                    Next
                </SovosButton>
            </SovosBox >
        </SovosBox >
    );
};

const MappingGridToolbar = ({ toolbarProps: tp, defaultToolbarProps }) => {
    const { hideDoNotImport, onToggleHideDoNotImport } = tp || {};
    const { isPreferencesPanelOpen, isFilterPanelOpen } = defaultToolbarProps || {};
    const gridRef = useGridApiContext();

    const toggleColumnPanel = () => {
        if (!isPreferencesPanelOpen) {
            gridRef.current.showPreferences(GridPreferencePanelsValue.columns);
        } else {
            gridRef.current.hidePreferences();
        }
    };

    const toggleFilterPanel = () => {
        if (!isFilterPanelOpen) {
            gridRef.current.showFilterPanel();
        } else {
            gridRef.current.hideFilterPanel();
        }
    };

    return (
        <SovosToolbarGroup sx={{ width: "100%" }}>
            <SovosToolbarActions>
                <SovosButton
                    variant="outlined"
                    size="small"
                    startIcon={hideDoNotImport ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    onClick={onToggleHideDoNotImport}
                    sx={{ fontSize: 12, textTransform: "none", height: 32 }}
                >
                    {hideDoNotImport ? "Show Do Not Import" : "Hide Do Not Import"}
                </SovosButton>
                <SovosToolbarColumn onClick={toggleColumnPanel} />
                <SovosToolbarFilter onClick={toggleFilterPanel} appliedFiltersCount={0} />
            </SovosToolbarActions>
        </SovosToolbarGroup>
    );
};

const ColumnMappingCanvas = ({
    frameworkName = "Framework Name",
    items = [],
    headerItems = [],
    profileId,
    projectId,
    fiscalYear,
    importId,
    projectModules = [],
    availableConfigSets: externalAvailableConfigSets,
    onFetchConfigSets,
    onFetchEntities,
    onFetchCdmFields,
    onFetchExistingMapping,
    onCreateMapping,
    onUpdateMapping,
    onRunMapping,
}) => {

    const { i18n } = useGlobalValue();
    const SYSTEM_COLUMNS = new Set(["CLB_ImportID", "FieldMetadata"]);
    const userHeaderItems = headerItems.filter((h) => !SYSTEM_COLUMNS.has(h.headerName));
    const csvColumns = userHeaderItems.map((h) => h.columnName);
    const [step, setStep] = useState(1);
    const [showEntityDialog, setShowEntityDialog] = useState(true);
    const [entityLabel, setEntityLabel] = useState("");
    const [selectedConfigSet, setSelectedConfigSet] = useState("");
    const [selectedEntityName, setSelectedEntityName] = useState("");
    const [selectedFileType, setSelectedFileType] = useState("");
    const [mschFields, setMschFields] = useState([]);
    const [mappings, setMappings] = useState({});
    const originalMappingsRef = useRef({});
    const [hideDoNotImport, setHideDoNotImport] = useState(true);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSkipProceedDialog, setShowSkipProceedDialog] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [imported, setImported] = useState(false);
    const [savedMappingId, setSavedMappingId] = useState(null);
    const [runningMapping, setRunningMapping] = useState(false);
    const [runResult, setRunResult] = useState(null);
    const [showFiscalYearDialog, setShowFiscalYearDialog] = useState(false);
    const [runFiscalYear, setRunFiscalYear] = useState("");
    const [savingMapping, setSavingMapping] = useState(false);
    const [fixedCells, setFixedCells] = useState(new Set());

    useEffect(() => {
        if (!profileId || !onFetchExistingMapping) {
            return;
        }
        onFetchExistingMapping(profileId).then((existing) => {
            if (!existing) {
                return;
            }
            const parsedMappings = JSON.parse(existing.column_mappings || "[]");
            const restoredMappings = parsedMappings.reduce(
                (acc, { sourceColumn, targetField }) => ({ ...acc, [sourceColumn]: targetField }),
                {},
            );
            setSelectedConfigSet(existing.config_set);
            setSelectedEntityName(existing.entity_name);
            setSelectedFileType(existing.file_type || "");
            setEntityLabel(existing.entity_name);
            setMappings(restoredMappings);
            originalMappingsRef.current = restoredMappings;
            setSavedMappingId(existing.id);
            onFetchCdmFields(existing.config_set, existing.entity_name).then((result) => {
                const fields = (result?.items || []).map((f) => ({
                    id: f.cdm_name,
                    name: f.db_name,
                    type: f.field_type?.startsWith("int") || f.field_type?.startsWith("decimal") ? "number"
                        : f.field_type?.startsWith("date") ? "date"
                            : "text",
                    nullable: f.nullable,
                }));
                setMschFields(fields);
            });
            setShowEntityDialog(false);
            setStep(2);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId]);

    const usedFields = Object.entries(mappings)
        .filter(([, v]) => v && v !== DO_NOT_IMPORT)
        .map(([, v]) => v);

    const totalColumns = csvColumns.length;
    const doNotImportCount = csvColumns.filter((c) => mappings[c] === DO_NOT_IMPORT).length;
    const mappedCount = csvColumns.filter((c) => mappings[c] && mappings[c] !== DO_NOT_IMPORT).length;
    const needsMappingCount = totalColumns - mappedCount - doNotImportCount;
    const readyCount = mappedCount;
    const skippedCount = doNotImportCount;

    const allMapped = needsMappingCount === 0;

    const parsedFieldMetadata = useMemo(() => {
        const raw = items[0]?.fieldMetadata;
        if (!raw) return {};
        try { return JSON.parse(raw); } catch { return {}; }
    }, [items]);

    const columnTypeMismatch = useMemo(() => {
        const result = {};
        csvColumns.forEach((col) => {
            const rawCol = stripCdmPrefix(col);
            const meta = parsedFieldMetadata[rawCol];
            if (!meta) return;
            const fieldId = mappings[col];
            const field = fieldId && fieldId !== DO_NOT_IMPORT
                ? mschFields.find((f) => f.id === fieldId)
                : null;
            if (!field) return;
            const sourceType = DATATYPE_TO_FIELD_TYPE[meta.dataType?.toUpperCase()];
            if (sourceType && sourceType !== field.type) {
                result[col] = { sourceType, targetType: field.type };
            }
        });
        return result;
    }, [csvColumns, mappings, mschFields, parsedFieldMetadata]);

    const columnWarnings = useMemo(() => {
        const counts = {};
        Object.keys(columnTypeMismatch).forEach((col) => {
            counts[col] = items.length;
        });
        return counts;
    }, [items.length, columnTypeMismatch]);

    const fixedCountByColumn = useMemo(() => {
        const counts = {};
        fixedCells.forEach((key) => {
            const col = key.slice(key.indexOf("__") + 2);
            counts[col] = (counts[col] || 0) + 1;
        });
        return counts;
    }, [fixedCells]);

    const effectiveWarnings = useMemo(() => {
        const result = {};
        Object.entries(columnWarnings).forEach(([col, count]) => {
            result[col] = Math.max(0, count - (fixedCountByColumn[col] || 0));
        });
        return result;
    }, [columnWarnings, fixedCountByColumn]);

    const handleMappingChange = useCallback((csvCol, fieldId) => {
        setMappings((prev) => ({ ...prev, [csvCol]: fieldId }));
    }, []);

    const handleRevert = useCallback((csvCol) => {
        const original = originalMappingsRef.current[csvCol] ?? null;
        setMappings((prev) => {
            const next = { ...prev };
            if (original === null) {
                delete next[csvCol];
            } else {
                next[csvCol] = original;
            }
            return next;
        });
    }, []);

    const handleEntityChosen = async ({ configSet, entityName, fileType, entityLabel: label }) => {
        setSelectedConfigSet(configSet);
        setSelectedEntityName(entityName);
        setSelectedFileType(fileType);
        setEntityLabel(label);
        setShowEntityDialog(false);
        setStep(2);
        const result = await onFetchCdmFields(configSet, entityName);
        const fields = (result?.items || []).map((f) => ({
            id: f.cdm_name,
            name: f.db_name,
            type: f.field_type?.startsWith("int") || f.field_type?.startsWith("decimal") ? "number"
                : f.field_type?.startsWith("date") ? "date"
                    : "text",
            nullable: f.nullable,
        }));
        setMschFields(fields);
    };

    const handleNext = () => {
        if (step === 2) setShowConfirmDialog(true);
    };

    const handleConfirm = () => {
        setShowConfirmDialog(false);
        setShowSuccessToast(true);
        setHideDoNotImport(true);
        setStep(3);
    };

    const handleStepClick = (stepNum) => {
        setStep(stepNum);
        if (stepNum === 1) {
            setShowEntityDialog(true);
        }
    };

    const remainingWarningRows = [];

    const skippedColumns = csvColumns.filter((c) => mappings[c] === DO_NOT_IMPORT);

    const validationColumnsForDialog = csvColumns.filter(
        (c) => mappings[c] && mappings[c] !== DO_NOT_IMPORT
    );

    const dialogTableColumns = validationColumnsForDialog;

    const saveMapping = async () => {
        setSavingMapping(true);
        const columnMappings = csvColumns
            .filter((col) => mappings[col] && mappings[col] !== DO_NOT_IMPORT)
            .map((col) => ({ sourceColumn: col, targetField: mappings[col] }));

        const serializedMappings = JSON.stringify(columnMappings);

        const result = savedMappingId
            ? await onUpdateMapping(savedMappingId, {
                configSet: selectedConfigSet,
                entityName: selectedEntityName,
                columnMappings: serializedMappings,
                fileType: selectedFileType,
            })
            : await onCreateMapping({
                profileId,
                mappingName: `${profileId}_${selectedConfigSet}_${selectedEntityName}`,
                configSet: selectedConfigSet,
                entityName: selectedEntityName,
                columnMappings: serializedMappings,
                fileType: selectedFileType,
            });

        setSavingMapping(false);

        if (result?.success) {
            setSavedMappingId(result.id || savedMappingId);
            setImported(true);
        }
    };

    const handleRunMapping = async (selectedFiscalYear) => {
        if (!savedMappingId || !profileId || !projectId || !selectedFiscalYear || !importId) return;
        setRunningMapping(true);
        setRunResult(null);
        const result = await onRunMapping({
            profileId,
            mappingId: savedMappingId,
            projectId: String(projectId),
            fiscalYear: String(selectedFiscalYear),
            importId,
        });
        setRunningMapping(false);
        setRunResult(result?.success ? "success" : "error");
    };

    const handleRunMappingConfirm = () => {
        setShowFiscalYearDialog(false);
        handleRunMapping(runFiscalYear);
    };

    const isValidFiscalYear = /^\d{4}$/.test(runFiscalYear);

    const handleProceedClick = () => {
        if (skippedColumns.length > 0 || remainingWarningRows.length > 0) {
            setShowSkipProceedDialog(true);
        } else {
            saveMapping();
        }
    };

    const handleSkipAndProceed = () => {
        setShowSkipProceedDialog(false);
        saveMapping();
    };

    const visibleCsvColumns = hideDoNotImport
        ? csvColumns.filter((c) => mappings[c] !== DO_NOT_IMPORT)
        : csvColumns;

    const visibleHeaderItems = userHeaderItems.filter((h) =>
        visibleCsvColumns.includes(h.columnName)
    );

    const dgHeader = visibleHeaderItems;
    const dgItems = buildDgItems(items, visibleCsvColumns);

    const dgAvailableFilters = visibleCsvColumns.map((col) => {
        const fieldId = mappings[col];
        const field = fieldId && fieldId !== DO_NOT_IMPORT
            ? mschFields.find((f) => f.id === fieldId)
            : null;
        const filterType = field?.type === "number" ? "int"
            : field?.type === "date" ? "date"
                : "string";
        return { prop: col, type: filterType, name: fieldId ? `${fieldId} (${col})` : col };
    });

    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const value = mappings[csvColumn] ?? null;

            return (
                <SovosBox
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        py: 0.5,
                        gap: 0.5,
                    }}
                >
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <SovosTypography
                            variant="caption"
                            fontWeight={600}
                            color="text.primary"
                            noWrap
                        >
                            {displayName}
                        </SovosTypography>
                    </SovosBox>
                    <MappingDropdown
                        csvColumn={csvColumn}
                        displayName={displayName}
                        value={value}
                        originalValue={originalMappingsRef.current[csvColumn] ?? null}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        onChange={handleMappingChange}
                        onRevert={handleRevert}
                        i18n={i18n}
                    />
                </SovosBox>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mappings, usedFields, mschFields, handleMappingChange, handleRevert]
    );

    const validationColumns = csvColumns.filter(
        (c) => mappings[c] && mappings[c] !== DO_NOT_IMPORT
    );

    const validationHeaderItems = userHeaderItems.filter((h) =>
        validationColumns.includes(h.columnName)
    );

    const validationDgHeader = validationHeaderItems.map((h) => ({
        ...h,
        basis: `${(100 / validationHeaderItems.length).toFixed(2)}%`,
        sortable: false,
        editable: true,
    }));

    const validationDgItems = buildDgItems(items, validationColumns);

    const totalWarnings = Object.values(effectiveWarnings).reduce((a, b) => a + b, 0);

    const validationHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const fieldId = mappings[csvColumn];
            const field = fieldId && fieldId !== DO_NOT_IMPORT
                ? mschFields.find((f) => f.id === fieldId)
                : null;
            const warningCount = effectiveWarnings[csvColumn] ?? 0;

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosBox
                        sx={{
                            display: "inline-flex",
                            alignSelf: "flex-start",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.5,
                            bgcolor: warningCount > 0 ? "error.50" : "success.50",
                        }}
                    >
                        <SovosTypography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: warningCount > 0 ? "error.main" : "success.dark", fontSize: 10 }}
                        >
                            {warningCount} warning{warningCount !== 1 ? "s" : ""}
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {field && <FieldTypeIcon type={field.type} size={13} />}
                        <SovosTypography variant="caption" fontWeight={600} noWrap>
                            {field ? field.name : displayName}
                        </SovosTypography>
                    </SovosBox>
                </SovosBox>
            );
        },
        [mappings, mschFields, effectiveWarnings]
    );

    const validationCellHighlight = useCallback(
        (params) => {
            if (!(columnWarnings[params.field] > 0)) return "";
            const cellKey = `${params.row?.uniqueId ?? params.id}__${params.field}`;
            return fixedCells.has(cellKey) ? "" : "validation-warning-cell";
        },
        [columnWarnings, fixedCells]
    );

    const handleValidationRowEdit = useCallback((newRow, oldRow) => {
        const changedCol = validationColumns.find((col) => newRow[col] !== oldRow[col]);
        const mismatch = changedCol && columnTypeMismatch[changedCol];
        if (!mismatch) return newRow;

        const cellKey = `${newRow.uniqueId}__${changedCol}`;
        const newValue = newRow[changedCol];
        const resolved = isCellMismatchResolved(newValue, mismatch.targetType, mismatch.sourceType);

        if (resolved) {
            setFixedCells((prev) => {
                if (prev.has(cellKey)) return prev;
                const next = new Set(prev);
                next.add(cellKey);
                return next;
            });
        } else {
            setFixedCells((prev) => {
                if (!prev.has(cellKey)) return prev;
                const next = new Set(prev);
                next.delete(cellKey);
                return next;
            });
        }
        return newRow;
    }, [validationColumns, columnTypeMismatch]);

    return (
        <SovosBox sx={{ p: 3, width: "100%", height: "100%", boxSizing: "border-box", position: "relative" }}>
            <SovosStepper activeStep={step - 1} sx={{ mb: 3 }}>
                {STEPS.map((label, idx) => {
                    const stepNum = idx + 1;
                    const isDone = imported ? true : stepNum < step;
                    return (
                        <SovosStep key={label} completed={isDone}>
                            {isDone ? (
                                <SovosStepButton onClick={() => handleStepClick(stepNum)}>
                                    {label}
                                </SovosStepButton>
                            ) : (
                                <SovosStepLabel>{label}</SovosStepLabel>
                            )}
                        </SovosStep>
                    );
                })}
            </SovosStepper>

            {/* Page header */}
            <SovosBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SovosTypography variant="h5" fontWeight={700}>
                        {frameworkName}
                    </SovosTypography>
                    {step <= 2 && (
                        <SovosBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 3,
                                bgcolor: "warning.50",
                                border: "1px solid",
                                borderColor: "warning.light",
                            }}
                        >
                            <SovosBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main" }} />
                            <SovosTypography variant="caption" color="warning.dark" fontWeight={600}>
                                {i18n`Under mapping`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {step === 3 && !imported && (
                        <SovosBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 3,
                                bgcolor: "primary.50",
                                border: "1px solid",
                                borderColor: "primary.light",
                            }}
                        >
                            <SovosBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                            <SovosTypography variant="caption" color="primary.dark" fontWeight={600}>
                                {i18n`Waiting import`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {imported && (
                        <SovosBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 3,
                                bgcolor: "success.50",
                                border: "1px solid",
                                borderColor: "success.light",
                            }}
                        >
                            <SovosBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
                            <SovosTypography variant="caption" color="success.dark" fontWeight={600}>
                                {i18n`Proceeded`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>

                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {step === 2 && (
                        <SovosButton variant="text" onClick={() => setShowEntityDialog(true)}>
                            {i18n`Cancel`}
                        </SovosButton>
                    )}
                    {step === 2 && (
                        <SovosButton
                            variant="contained"
                            disabled={!allMapped}
                            onClick={handleNext}
                        >
                            Next
                        </SovosButton>
                    )}
                    {step === 3 && !imported && (
                        <SovosButton variant="text" onClick={() => setStep(2)}>
                            Back
                        </SovosButton>
                    )}
                    {step === 3 && !imported && (
                        <SovosButton
                            variant="contained"
                            startIcon={<CheckCircle fontSize="small" />}
                            onClick={handleProceedClick}
                            disabled={savingMapping}
                        >
                            {savingMapping ? "Saving..." : "Proceed"}
                        </SovosButton>
                    )}
                </SovosBox>
            </SovosBox>

            {/* KPI bar */}
            {step <= 2 && (
                <SovosBox sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                    <StatTile label="Columns need to be mapped" value={needsMappingCount} borderColor={KPI_BORDER_COLORS[0]} />
                    <StatTile label="Ready for next step" value={readyCount} borderColor={KPI_BORDER_COLORS[1]} />
                    <StatTile label="Columns skipped mapping" value={skippedCount} borderColor={KPI_BORDER_COLORS[2]} />
                    <StatTile label="All columns" value={totalColumns} borderColor={KPI_BORDER_COLORS[3]} />
                </SovosBox>
            )}
            {step === 3 && !imported && (
                <SovosBox sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                    <StatTile label="Data warnings" value={totalWarnings} borderColor={KPI_BORDER_COLORS[0]} />
                    <StatTile label="Fixed data" value={Object.values(fixedCountByColumn).reduce((a, b) => a + b, 0)} borderColor={KPI_BORDER_COLORS[1]} />
                </SovosBox>
            )}

            {/* Context line */}
            <SovosBox sx={{ mb: 1.5 }}>
                {step <= 2 && (
                    <SovosTypography variant="body2" color="text.secondary">
                        {i18n`You are mapping imported data to`}{" "}<strong>{i18n`${entityLabel}`}</strong>
                        {!showEntityDialog && (
                            <SovosButton
                                variant="text"
                                size="small"
                                sx={{ ml: 1, fontSize: 12, p: 0, minWidth: 0 }}
                                onClick={() => setShowEntityDialog(true)}
                            >
                                Change
                            </SovosButton>
                        )}
                    </SovosTypography>
                )}
                {step === 3 && !imported && (
                    <SovosTypography variant="body2" color="text.secondary">
                        {i18n`You are validating data format. Fix or proceed with warning`}
                    </SovosTypography>
                )}
            </SovosBox>

            {/* ── Import complete screen ── */}
            {imported && (
                <SovosBox
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 8,
                    }}
                >
                    <SovosBox sx={{ position: "relative", mb: 4 }}>
                        <SovosBox
                            sx={{
                                width: 140,
                                height: 140,
                                borderRadius: "50%",
                                bgcolor: "success.50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <SovosBox
                                sx={{
                                    width: 80,
                                    height: 96,
                                    border: "2px dashed",
                                    borderColor: "primary.main",
                                    borderRadius: 1.5,
                                    bgcolor: "background.paper",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    p: 1.5,
                                    justifyContent: "center",
                                }}
                            >
                                {[1, 2, 3, 4].map((i) => (
                                    <SovosBox key={i} sx={{ height: 7, borderRadius: 0.5, bgcolor: "grey.200" }} />
                                ))}
                            </SovosBox>
                        </SovosBox>
                        <SovosBox
                            sx={{
                                position: "absolute",
                                bottom: 6,
                                right: 6,
                                bgcolor: "success.main",
                                borderRadius: "50%",
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: 2,
                            }}
                        >
                            <CheckCircle sx={{ color: "white", fontSize: 22 }} />
                        </SovosBox>
                    </SovosBox>

                    <SovosTypography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                        {i18n`Mapping saved`}
                    </SovosTypography>
                    <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        {i18n`Your column mapping has been saved. Run the mapping to import data into the target entity.`}
                    </SovosTypography>
                    {runResult === "success" && (
                        <SovosTypography variant="body2" color="success.main" sx={{ mb: 2 }}>
                            {i18n`Mapping run submitted successfully. The loader will process your data shortly.`}
                        </SovosTypography>
                    )}
                    {runResult === "error" && (
                        <SovosTypography variant="body2" color="error.main" sx={{ mb: 2 }}>
                            {i18n`Failed to run mapping. Please try again.`}
                        </SovosTypography>
                    )}
                    <SovosButton
                        variant="contained"
                        onClick={() => { setRunFiscalYear(""); setShowFiscalYearDialog(true); }}
                        disabled={runningMapping || runResult === "success"}
                    >
                        {runningMapping ? i18n`Running...` : i18n`Run Mapping`}
                    </SovosButton>
                </SovosBox>
            )}

            <SovosDialog
                open={showFiscalYearDialog}
                onClose={() => setShowFiscalYearDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <SovosDialogTitle>
                    {i18n`Select Fiscal Year`}
                    <SovosIconButton
                        onClick={() => setShowFiscalYearDialog(false)}
                        tooltipText="Close"
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <Close />
                    </SovosIconButton>
                </SovosDialogTitle>
                <SovosDialogContent>
                    <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {i18n`Enter the fiscal year you want to map data into. This determines the target period in the repository.`}
                    </SovosTypography>
                    <SovosTextField
                        label={i18n`Fiscal Year`}
                        value={runFiscalYear}
                        onChange={(e) => setRunFiscalYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="e.g. 2024"
                        fullWidth
                        autoFocus
                        inputProps={{ maxLength: 4 }}
                        onKeyDown={(e) => { if (e.key === "Enter" && isValidFiscalYear) handleRunMappingConfirm(); }}
                    />
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton onClick={() => setShowFiscalYearDialog(false)}>
                        {i18n`Cancel`}
                    </SovosButton>
                    <SovosButton
                        variant="contained"
                        onClick={handleRunMappingConfirm}
                        disabled={!isValidFiscalYear}
                    >
                        {i18n`Run`}
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>

            {/* ── DataGrid: dimmed and non-interactive until entity is chosen ── */}
            <SovosBox
                sx={{
                    opacity: !entityLabel ? 0.4 : 1,
                    pointerEvents: !entityLabel ? "none" : "auto",
                    transition: "opacity 0.2s ease",
                    display: imported ? "none" : undefined,
                }}
            >
                {/* Step 1 & 2 — column mapping grid (shown as background in step 1) */}
                {step <= 2 && (
                    <DataGrid
                        id="column-mapping-grid"
                        items={dgItems}
                        header={dgHeader}
                        card={rows.CellCard}
                        status={{ loading: false }}
                        metadata={{ total: dgItems.length }}
                        enablePagination
                        headerHeight={80}
                        columnHeaderRenderer={columnHeaderRenderer}
                        availableFilters={dgAvailableFilters}
                        toolbar={MappingGridToolbar}
                        toolbarProps={{
                            hideDoNotImport,
                            onToggleHideDoNotImport: () => setHideDoNotImport((h) => !h),
                        }}
                        showCheckbox={false}
                        modifierssx={{
                            "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                            "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                        }}
                    />
                )}

                {/* Step 3 — data validation grid */}
                {step === 3 && (
                    <DataGrid
                        id="column-validation-grid"
                        items={validationDgItems}
                        header={validationDgHeader}
                        card={rows.CellCard}
                        status={{ loading: false }}
                        metadata={{ total: validationDgItems.length }}
                        enablePagination
                        headerHeight={70}
                        columnHeaderRenderer={validationHeaderRenderer}
                        showCheckbox={true}
                        editMode
                        onRowEdit={handleValidationRowEdit}
                        onCellHighlight={validationCellHighlight}
                        modifierssx={{
                            "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                            "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                            "& .validation-warning-cell": {
                                backgroundColor: "rgba(211, 47, 47, 0.08) !important",
                            },
                        }}
                    />
                )}
            </SovosBox>{/* end dimmed grid wrapper */}

            {/* ── Step 1: Choose entity — inline overlay ── */}
            {showEntityDialog && (
                <SovosBox
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ChooseEntityCard
                        onNext={handleEntityChosen}
                        onCancel={() => setShowEntityDialog(false)}
                        i18n={i18n}
                        projectModules={projectModules}
                        availableConfigSets={externalAvailableConfigSets}
                        onFetchConfigSets={onFetchConfigSets}
                        onFetchEntities={onFetchEntities}
                        initialConfigSet={selectedConfigSet}
                        initialEntityName={selectedEntityName}
                    />
                </SovosBox>
            )}

            <ConfirmDialog
                open={showConfirmDialog}
                onCancel={() => setShowConfirmDialog(false)}
                onConfirm={handleConfirm}
                mappings={mappings}
                mschFields={mschFields}
                i18n={i18n}
            />

            <WarningSkipDialog
                open={showSkipProceedDialog}
                onCancel={() => setShowSkipProceedDialog(false)}
                onConfirm={handleSkipAndProceed}
                skippedColumns={skippedColumns}
                warningRows={remainingWarningRows}
                tableColumns={dialogTableColumns}
                i18n={i18n}
            />

            <SovosSnackbar
                open={showSuccessToast}
                message="You have finished column mapping. Let's continue with data validation!"
                onClose={() => setShowSuccessToast(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
};

export default ColumnMappingCanvas;
